import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, CARD_DESCRIPTOR, siteImages } from '../data/mock';
import { Shield, Eye } from 'lucide-react';

const PrivacyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Clarity Lens Co';
  }, []);

  const sections = [
    {
      title: 'Information We Collect',
      content: `We collect information you provide directly to us, such as when you create an account, make a purchase, or contact us for support. This may include your name, email address, shipping address, phone number, and payment information.`
    },
    {
      title: 'How We Use Your Information',
      content: `We use the information we collect to process your orders, communicate with you about your purchases, send promotional communications (with your consent), improve our website and services, and comply with legal obligations.`
    },
    {
      title: 'Information Sharing',
      content: `We do not sell your personal information to third parties. We may share your information with service providers who assist us in operating our website, processing payments, and delivering products. These parties are obligated to keep your information confidential.`
    },
    {
      title: 'Payment Security',
      content: `All payment transactions are processed through secure payment gateways. Your payment information is encrypted and we do not store your full credit card details on our servers. Charges will appear as "${CARD_DESCRIPTOR}" on your credit card statement.`
    },
    {
      title: 'Cookies and Tracking',
      content: `We use cookies and similar technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookie settings through your browser preferences.`
    },
    {
      title: 'Data Retention',
      content: `We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law.`
    },
    {
      title: 'Your Rights',
      content: `You have the right to access, correct, or delete your personal information. You may also opt out of marketing communications at any time. To exercise these rights, please contact us using the information below.`
    },
    {
      title: 'Changes to This Policy',
      content: `We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the effective date.`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Hero Banner */}
        <section className="relative h-[200px] overflow-hidden bg-neutral-100">
          <img 
            src={siteImages.privacyBanner}
            alt="Privacy Policy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-lg">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white text-xs font-medium mb-4">
                  <Shield className="w-3.5 h-3.5" />
                  Your Privacy Matters
                </span>
                <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold tracking-tight">
                  Privacy Policy
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-neutral-50 rounded-2xl p-8 md:p-12">
            <p className="text-neutral-500 mb-8 text-sm">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            
            <p className="text-neutral-700 mb-8 leading-relaxed">
              At Clarity Lens Co, operated by {CONTACT_INFO.corpName}, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you visit our website or make a purchase.
            </p>

            <div className="space-y-8">
              {sections.map((section, i) => (
                <div key={i}>
                  <h2 className="font-medium text-neutral-900 text-lg mb-3">{section.title}</h2>
                  <p className="text-neutral-600 leading-relaxed">{section.content}</p>
                </div>
              ))}
            </div>

            {/* Contact */}
            <div className="mt-12 pt-8 border-t border-neutral-200">
              <h2 className="font-medium text-neutral-900 text-lg mb-4">Contact Us</h2>
              <p className="text-neutral-600 mb-4">
                If you have questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-white rounded-xl p-6">
                <p className="text-neutral-900 font-medium">{CONTACT_INFO.corpName}</p>
                <p className="text-neutral-600 mt-2">
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                  {CONTACT_INFO.address.country}
                </p>
                <p className="text-neutral-600 mt-2">
                  Email: <a href={`mailto:${CONTACT_INFO.email}`} className="text-neutral-900 hover:underline">{CONTACT_INFO.email}</a>
                </p>
                <p className="text-neutral-600">
                  Phone: <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-neutral-900 hover:underline">{CONTACT_INFO.phone}</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPage;
